import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PeopleIcon from '@mui/icons-material/People';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import DataGrid from 'app/components/DataGrid';
import classes from 'app/components/DataGrid/classes.module.scss';
import useJobs from 'app/hooks/useJobs';
import JobModal from './JobModal';
import MomentsCell from 'app/components/DataGrid/MomentsCell';
import CustomersCell from 'app/components/DataGrid/CustomersCell';
import useDeeplink from 'app/hooks/useDeeplink';
import JobUploadProgressBar from './JobUploadProgressBar';

import {
  getJobs,
  setJobParams
} from 'app/actions/jobs';

import {
  getJob,
  getJobs as exportJobsSvc
} from 'app/services/jobs';

import {
  getJobConfigs
} from 'app/services/jobConfigs';

import {
  getKiosksV2
} from 'app/services/kiosks';

import {
  searchCustomers
} from 'app/services/customers';

import {
  setMomentParams
} from 'app/actions/moments';

import {
  setPhotoSwipeOpen,
  setPhotoSwipeMoment
} from 'app/actions/photoSwipeActions';

import {
  AND_OPERATOR,
} from 'app/hooks/useFilter';

import {
  formatCustomerSelectLabel,
  formatJobConfigCellValue,
  formatKioskCellValue
} from 'app/utils/valueFormatters';

import {
  formatOptions
} from 'app/utils/options';

import {
  customers,
  moments
} from 'app/resources';

import {
  getLocalizedDateTime
} from 'shared/utils/Utils';

const Jobs = props => {
  const dispatch = useDispatch();
  const account = useSelector(state => state.portalReducer.account);
  const user = useSelector(state => state.userReducer.user);

  // load needed resources on first render
  const [jobConfigs, setJobConfigs] = useState([]);
  const [kiosks, setKiosks] = useState([]);

  useEffect(() => {
    getJobConfigs({ page: 1, per_page: 100 })
      .then(resp => setJobConfigs(resp.records));

    getKiosksV2({ page: 1, per_page: 100 })
      .then(resp => setKiosks(resp.records));
  }, []);

  const {
    resource,
    formIsOpen,
    currentRecord,
    onFormClose,
    onAdd,
    onEdit,
    onQuickAdd,
    onQuickEdit,
    onDataGridLoad,
    onFormSubmit,
    onDestroy,
    showFilters,
    getRowData,
    updateRow,
    getFilterModel
  } = useJobs();

  const exportCSV = () => {
    let params = {
      page: 0,
      per_page: 1000,
      is_export: true
    }

    const filterModel = getFilterModel();
    if (filterModel) {
      params.filter_model = filterModel;
    }

    exportJobsSvc(params).then((resp => resp.blob())).then((blob) => {
      const elem = window.document.createElement('a');
      elem.href = window.URL.createObjectURL(blob);
      elem.download = `jobs_${new Date().toISOString()}.csv`;
      document.body.appendChild(elem);
      elem.click();
      document.body.removeChild(elem);
    })
  }

  const deeplink = useDeeplink();

  const viewCustomersForJob = jobId => {
    // filter customers by job ahead of showing the customers table.
    // this will overwrite any filters previously set.
    // currently the filter expects values to be strings
    let filterModel = {
      job_id: [{
        filterClass: 'text',
        filterType: 'equals',
        value: jobId.toString(),
        operator: AND_OPERATOR
      }]
    }

    deeplink.load(customers, filterModel);
  }

  const viewDefaultMomentsForJob = async jobId => {
    // filter moments by job ahead of showing the moments table.
    // this will overwrite any filters previously set.
    // currently the filter expects values to be strings
    let filterModel = {
      job_id: [{
        filterClass: 'text',
        filterType: 'equals',
        value: jobId.toString(),
        operator: AND_OPERATOR
      }],
      is_default: [{
        filterClass: 'text',
        filterType: 'equals',
        value: '1',
        operator: AND_OPERATOR
      }]
    }

    deeplink.load(moments, filterModel);
  }

  const viewCustomerMomentsForJob = async jobId => {
    // filter moments by job ahead of showing the moments table.
    // this will overwrite any filters previously set.
    // currently the filter expects values to be strings
    let filterModel = {
      job_id: [{
        filterClass: 'text',
        filterType: 'equals',
        value: jobId.toString(),
        operator: AND_OPERATOR
      }],
      is_default: [{
        filterClass: 'text',
        filterType: 'equals',
        value: '0',
        operator: AND_OPERATOR
      }]
    }

    deeplink.load(moments, filterModel);
  }

  const getCustomerOptions = async (page, perPage, filterValue) => {
    let customers = await searchCustomers(page, perPage, filterValue);

    return formatOptions(customers, {
      label: formatCustomerSelectLabel
    });
  }

  const header = 'Jobs';
  const addButtonLabel = 'Add Job';

  const columnOrder = [
    'default_moments',
    'customer_moments',
    'upload_status',
    'main_job',
    'event_date',
    'name',
    'customer_id',
    'job_config',
    'kiosk',
    'viewed_count',
    'purchase_count',
    'percent_viewed',
    'percent_purchased',
    'average_order_value',
    'purchase_total'
  ];

  const onlyColumns = [
    'id',
    'created_at',
    'updated_at',
    'start_date',
    'end_date',
    'event_id',
    'event_date',
    'name',
    'job_config',
    'hashtag',
    'default_fee',
    'description',
    'pre_signup',
    'max_registration',
    'kiosk_id',
    'account_id',
    'provider_account_id',
    'lab_name',
    'provider_name',
    'customer_id',
    'customer_count',
    'viewed_count',
    'purchase_count',
    'percent_viewed',
    'percent_purchased',
    'average_order_value',
    'purchase_total',
    'default_moments',
    'customer_moments',
    'upload_status',
    'main_job'
  ];

  const visibleColumns = [
    'upload_status',
    'main_job',
    'event_date',
    'name',
    'default_moments',
    'customer_moments',
    'customer_id',
    'job_config',
    'viewed_count',
    'purchase_count',
    'percent_viewed',
    'percent_purchased',
    'average_order_value',
    'purchase_total'
  ]

  // only show Default Moments if feature flag is enabled
  let omitColumns = [];
  if (!account.feature_flags.default_moments) {
    omitColumns.push('default_moments');
  }

  const jobConfigOptions = formatOptions(jobConfigs);
  const kioskOptions = formatOptions(kiosks);

  const onMomentClick = moment => {
    dispatch(setPhotoSwipeMoment(moment));
    dispatch(setPhotoSwipeOpen(true));
  }

  const setPhotoSwipeItem = job => {
    if (job.cover_moment) {
      return {
        id: job.cover_moment.id,
        src: job.cover_moment.preview_url,
        w: job.cover_moment.width,
        h: job.cover_moment.height,
        digital_edit: job.cover_moment.digital_edit,
        orig_moment_id: job.cover_moment.orig_moment_id,
      }
    }
  };

  // setup callbacks for uploads via both modal and grid cell
  const onUploadStart = (upload, { job }) => {
    job.last_upload = upload;
    updateRow(job);
  }

  const onUploadComplete = async (upload, { job }) => {
    let updatedJob = await getJob(job.id);
    updatedJob.last_upload = upload;
    updateRow(updatedJob);
  }

  const columnDefOverrides = [
    {
      field: 'default_moments',
      headerName: 'Default Moments',
      pinned: 'left',
      inputType: 'number',
      width: 11 * 16,
      thumbnail: data => data?.default_cover_moment?.thumb_url,
      onThumbnailClick: data => onMomentClick(data.cover_moment),
      cellRenderer: params => {
        const {
          data,
          colDef
        } = params;

        if (data) {
          const {
            id,
            actual_photo_count,
            template_story_id
          } = data;

          const onImagesClick = () => viewDefaultMomentsForJob(id);

          // disabling the upload label for now,
          // as uploads from here are disabled
          let showUploadLabel = false;
          // only show UPLOAD label if no images
          //let showUploadLabel = !total_photo_count;

          return (
            <MomentsCell
              data={data}
              colDef={colDef}
              showImageCount={true}
              imageCount={actual_photo_count}
              onImagesClick={onImagesClick}
              showUpload={false}
              showUploadLabel={showUploadLabel}
              uploadJobId={id}
              uploadStoryIds={[template_story_id]}
              uploadEntityId={id}
              onUploadStart={onUploadStart}
              onUploadComplete={onUploadComplete}
              job={data}
            />
          );
        }
        return '';
      }
    },
    {
      field: 'customer_moments',
      headerName: 'Customer Moments',
      pinned: 'left',
      inputType: 'number',
      width: 11 * 16,
      thumbnail: data => data?.customer_cover_moment?.thumb_url,
      onThumbnailClick: data => onMomentClick(data.cover_moment),
      cellRenderer: params => {
        const {
          data,
          colDef
        } = params;

        if (data) {
          const {
            id,
            customer_photo_count,
            template_story_id
          } = data;

          const onImagesClick = () => viewCustomerMomentsForJob(id);

          // disabling the upload label for now,
          // as uploads from here are disabled
          let showUploadLabel = false;
          // only show UPLOAD label if no images
          //let showUploadLabel = !total_photo_count;

          return (
            <MomentsCell
              data={data}
              colDef={colDef}
              showImageCount={true}
              imageCount={customer_photo_count}
              onImagesClick={onImagesClick}
              showUpload={false}
              showUploadLabel={showUploadLabel}
              uploadJobId={id}
              uploadStoryIds={[template_story_id]}
              uploadEntityId={id}
              onUploadStart={onUploadStart}
              onUploadComplete={onUploadComplete}
              job={data}
            />
          );
        }
        return '';
      }
    },
    {
      field: 'upload_status',
      headerName: 'Last Upload / Status',
      excludeFromMaxWidth: true,
      cellRenderer: params => {
        let { data } = params;
        let upload = data?.last_upload;
        if (upload) {
          if (upload.completed_at) {
            let lastUpload = getLocalizedDateTime(user.locale, upload.completed_at);
            return <span className={classes.gridCell}>{lastUpload}</span>;
          } else {
            return <JobUploadProgressBar job={data} />;
          }
        }
        return '';
      }
    },
    {
      field: 'event_date',
      headerName: 'Event Date',
      minWidth: 20 * 16, // 20rem
      inputType: 'date',
      renderFields: [
        { field: 'start_date', type: 'date' },
        { field: 'created_at', type: 'date', dateStyle: 'short', prefix: 'Created ' }
      ]
    },
    {
      field: 'name',
      minWidth: 20 * 16, // 20rem
      quickEdit: true,
      renderFields: ['name', 'event_id']
    },
    { field: 'event_id', headerName: 'Job ID' },
    {
      field: 'job_config',
      headerName: 'Product',
      valueFormatter: value => formatJobConfigCellValue(value),
      inputType: 'select',
      inputOptions: jobConfigOptions,
      quickEdit: true
    },
    {
      field: 'customer_id',
      headerName: 'Customers',
      minWidth: 250,
      cellRenderer: params => {
        let data = params.data;
        if (data) {
          const {
            id,
            customer_count,
            matched_customers
          } = data;

          const onClick = () => viewCustomersForJob(id);

          // show customers that matched from filter
          let matchedCustomers = matched_customers?.filter(customer => {
            return customer.job_id === id;
          }) || [];

          return (
            <CustomersCell
              customerCount={customer_count}
              onClick={onClick}
              matchedCustomers={matchedCustomers}
            />
          );
        }
        return '';
      },
      inputType: 'autocomplete',
      getInputOptions: getCustomerOptions,
      selectedOptionFormatter: data => formatOptions([data.customer])[0]
    },
    { field: 'customer_count', inputType: 'number', minWidth: 8 * 16 },
    { field: 'created_at', formatType: 'date', inputType: 'date' },
    { field: 'updated_at', formatType: 'date', inputType: 'date' },
    { field: 'start_date', formatType: 'date', inputType: 'date' },
    { field: 'end_date', formatType: 'date', inputType: 'date' },
    {
      field: 'percent_viewed',
      headerName: '% Viewed',
      sortable: false,
      inputType: 'number',
      formatType: 'percent'
    },
    {
      field: 'percent_purchased',
      headerName: '% Purchased',
      sortable: false,
      inputType: 'number',
      formatType: 'percent'
    },
    { field: 'viewed_count', inputType: 'number' },
    { field: 'purchase_count', inputType: 'number' },
    { field: 'purchase_total', sortable: false, filterDisabled: true },
    { field: 'average_order_value', sortable: false, filterDisabled: true },
    { field: 'id', minWidth: 10 * 16 }
  ]

  if (kiosks?.length) {
    onlyColumns.push('kiosk');
    visibleColumns.push('kiosk');

    columnDefOverrides.push({
      field: 'kiosk',
      headerName: 'Kiosk',
      valueFormatter: value => formatKioskCellValue(value),
      inputType: 'select',
      inputOptions: kioskOptions,
      quickEdit: true
    });
  }

  // if sub jobs are enabled then show main job column
  if (account.feature_flags.sub_jobs) {
    onlyColumns.push('main_job');
    visibleColumns.push('main_job');

    columnDefOverrides.push({
      field: 'main_job',
      headerName: 'Main Job',
      renderFields: ['main_job.name', 'main_job.event_id']
    });
  }

  const callback = selectedRows => {
    console.log(selectedRows);
  }

  const actions = [
    { key: 'destroy', label: 'Delete Jobs', callback: onDestroy }
  ]

  const buttons = [
    {
      key: 'export',
      icon: <FileDownloadIcon />,
      label: 'CSV',
      onClick: exportCSV
    }
  ]

  return (
    <div>
      <DataGrid
        resource={resource}
        header={header}
        getRowData={getRowData}
        addButtonLabel={addButtonLabel}
        onAdd={onAdd}
        showEdit={true}
        onEdit={onEdit}
        showQuickEdit={true}
        onQuickAdd={onQuickAdd}
        onQuickEdit={onQuickEdit}
        columnOrder={columnOrder}
        columnDefOverrides={columnDefOverrides}
        setPhotoSwipeItem={setPhotoSwipeItem}
        onlyColumns={onlyColumns}
        visibleColumns={visibleColumns}
        omitColumns={omitColumns}
        showFilters={showFilters}
        showSelect={true}
        actions={actions}
        loadingMessage="Loading jobs..."
        onVisibleColumnsUpdate={columns => { console.log(columns) }}
        onDataGridLoad={onDataGridLoad}
        buttons={buttons}
      />
      <JobModal
        isOpen={formIsOpen}
        onClose={onFormClose}
        job={currentRecord}
        jobConfigs={jobConfigs}
        kiosks={kiosks}
        onFormSubmit={onFormSubmit}
        onDestroy={onDestroy}
        onUploadStart={onUploadStart}
        onUploadComplete={onUploadComplete}
        updateRow={updateRow}
      />
    </div>
  );
}

export default Jobs;
